import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { NumberRange } from "../UI/Table/numberRange.ts";
import { SortDirection } from "../UI/Table/TableColumnHeader.tsx";

const TEACHER_TABLE_SORT_FIELDS = [
  "name",
  "phoneNumber",
  "finalScore",
  "reviewCount",
] as const;

export type TeacherTableSortField = (typeof TEACHER_TABLE_SORT_FIELDS)[number];

export type TeacherTableSort = {
  sortBy: TeacherTableSortField;
  sortDirection: SortDirection;
};

export type TeacherTableFilters = {
  finalScore: NumberRange;
  reviewCount: NumberRange;
};

const DEFAULT_TEACHER_TABLE_SORT: TeacherTableSort = {
  sortBy: "finalScore",
  sortDirection: "desc",
};

const RANGE_FILTER_URL_PARAMS: Record<
  keyof TeacherTableFilters,
  { min: string; max: string }
> = {
  finalScore: { min: "scoreMin", max: "scoreMax" },
  reviewCount: { min: "reviewsMin", max: "reviewsMax" },
};

function isTeacherTableSortField(
  value: string | undefined,
): value is TeacherTableSortField {
  return (
    TEACHER_TABLE_SORT_FIELDS as readonly (string | undefined)[]
  ).includes(value);
}

function parseSort(value: string | null): TeacherTableSort {
  const [sortBy, sortDirection] = value?.split(":") ?? [];
  const isKnownDirection = sortDirection === "asc" || sortDirection === "desc";
  return isTeacherTableSortField(sortBy) && isKnownDirection
    ? { sortBy, sortDirection }
    : DEFAULT_TEACHER_TABLE_SORT;
}

function parseNumber(value: string | null): number | undefined {
  if (!value) return undefined;
  const number = Number(value);
  return Number.isFinite(number) ? number : undefined;
}

function parseRange(
  params: URLSearchParams,
  urlParams: { min: string; max: string },
): NumberRange {
  return {
    min: parseNumber(params.get(urlParams.min)),
    max: parseNumber(params.get(urlParams.max)),
  };
}

function setOrDelete(
  params: URLSearchParams,
  name: string,
  value: string | undefined,
) {
  if (value === undefined) params.delete(name);
  else params.set(name, value);
}

function isDefaultSort(sort: TeacherTableSort): boolean {
  return (
    sort.sortBy === DEFAULT_TEACHER_TABLE_SORT.sortBy &&
    sort.sortDirection === DEFAULT_TEACHER_TABLE_SORT.sortDirection
  );
}

export function useTeacherTableUrlState() {
  const [searchParams, setSearchParams] = useSearchParams();

  const sort = useMemo(
    () => parseSort(searchParams.get("sort")),
    [searchParams],
  );

  const filters = useMemo<TeacherTableFilters>(
    () => ({
      finalScore: parseRange(searchParams, RANGE_FILTER_URL_PARAMS.finalScore),
      reviewCount: parseRange(
        searchParams,
        RANGE_FILTER_URL_PARAMS.reviewCount,
      ),
    }),
    [searchParams],
  );

  const setSort = useCallback(
    (nextSort: TeacherTableSort) =>
      setSearchParams(
        (current) => {
          const params = new URLSearchParams(current);
          setOrDelete(
            params,
            "sort",
            isDefaultSort(nextSort)
              ? undefined
              : `${nextSort.sortBy}:${nextSort.sortDirection}`,
          );
          return params;
        },
        { replace: true },
      ),
    [setSearchParams],
  );

  const setFilters = useCallback(
    (nextFilters: TeacherTableFilters) =>
      setSearchParams(
        (current) => {
          const params = new URLSearchParams(current);
          Object.entries(RANGE_FILTER_URL_PARAMS).forEach(
            ([field, urlParams]) => {
              const range = nextFilters[field as keyof TeacherTableFilters];
              setOrDelete(params, urlParams.min, range.min?.toString());
              setOrDelete(params, urlParams.max, range.max?.toString());
            },
          );
          return params;
        },
        { replace: true },
      ),
    [setSearchParams],
  );

  return { sort, filters, setSort, setFilters };
}
